import { ReactPlugin } from '@microsoft/applicationinsights-react-js';
import { ApplicationInsights, type IPageViewTelemetry } from '@microsoft/applicationinsights-web';
import type { EnvConfig } from '@/lib/env-config';

let appInsights: ApplicationInsights | null = null;
let reactPlugin: ReactPlugin | null = null;

export function initAppInsights(envConfig: EnvConfig): void {
  if (appInsights) return; // idempotent

  reactPlugin = new ReactPlugin();

  appInsights = new ApplicationInsights({
    config: {
      connectionString: envConfig.appInsightsConnectionString || '',
      maxAjaxCallsPerView: 2000, // Increase from default 500 to handle polling during uploads
      extensions: [reactPlugin],
      extensionConfig: {
        [reactPlugin.identifier]: {}
      },
      enableAutoRouteTracking: false,
      enableCorsCorrelation: true,
      enableRequestHeaderTracking: true,
      enableResponseHeaderTracking: true,
      enableAjaxPerfTracking: true,
      disableFetchTracking: false,
      correlationHeaderExcludedDomains: [
        'localhost',
        '127.0.0.1',
        '*.netdocuments.app',
        '*.netdocuments.com',
      ],
      disableExceptionTracking: false,
      enableDebug: import.meta.env.DEV,
      loggingLevelConsole: import.meta.env.DEV ? 2 : 1,
      loggingLevelTelemetry: 1,
      samplingPercentage: 100,
      disableDataLossAnalysis: false,
      isCookieUseDisabled: false,
      isRetryDisabled: false,
      isStorageUseDisabled: false,
      isBeaconApiDisabled: false,
      namePrefix: 'crimson',
    }
  });

  appInsights.loadAppInsights();

  // Filter out benign ResizeObserver errors that flood logs
  // https://github.com/WICG/resize-observer/issues/38
  appInsights.addTelemetryInitializer((envelope) => {
    const data = envelope.baseData;
    if (data) {
      if (data.exceptions?.some((e: any) =>
        e.message?.includes('ResizeObserver loop completed with undelivered notifications')
      )) {
        return false;
      }
      if (data.message?.includes('ResizeObserver loop completed with undelivered notifications')) {
        return false;
      }
    }
  });
}

// Custom telemetry functions with null checks
export const trackEvent = (name: string, properties?: { [key: string]: any }, measurements?: { [key: string]: number }) => {
  if (appInsights?.trackEvent) {
    appInsights.trackEvent({ name, properties, measurements });
  }
};

const setPageViewTraceContext = (name: string) => {
  if (!appInsights) return;

  const traceContext = appInsights.getTraceCtx();
  if (!traceContext) return;

  traceContext.traceId = generateUUID().replace(/-/g, '');
  traceContext.pageName = name;
  appInsights.setTraceCtx(traceContext);
};

export const trackPageView = (pageView: IPageViewTelemetry) => {
  if (appInsights?.trackPageView) {
    const pageName = pageView.name || pageView.uri;
    if (pageName) {
      setPageViewTraceContext(pageName);
    }

    appInsights.trackPageView(pageView);
  }
};

export const trackException = (error: Error, severityLevel?: number, properties?: { [key: string]: any }) => {
  if (appInsights?.trackException) {
    appInsights.trackException({ error, severityLevel }, properties);
  }
};

export const trackTrace = (message: string, severityLevel?: number, properties?: { [key: string]: any }) => {
  if (appInsights?.trackTrace) {
    appInsights.trackTrace({ message, severityLevel }, properties);
  }
};

export const trackDependency = (name: string, commandName: string, duration: number, success: boolean, resultCode: number) => {
  if (appInsights?.trackDependencyData) {
    appInsights.trackDependencyData({
      id: generateUUID(),
      name,
      duration,
      success,
      responseCode: resultCode,
      type: 'Ajax',
      data: commandName,
      target: name,
    });
  }
};

// Add correlation ID to all requests
export const getCorrelationId = (): string => {
  if (typeof window !== 'undefined') {
    let correlationId = sessionStorage.getItem('correlationId');
    if (!correlationId) {
      correlationId = generateUUID();
      sessionStorage.setItem('correlationId', correlationId);
    }
    return correlationId;
  }
  return generateUUID();
};

function generateUUID(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

export const setAppInsightsUser = (username: string | null | undefined) => {
  if (!appInsights) return;

  if (username) {
    appInsights.context.user.accountId = username;
    appInsights.setAuthenticatedUserContext(username);
    return;
  }

  appInsights.clearAuthenticatedUserContext();
};

export const flushAppInsights = () => {
  appInsights?.flush();
};
